import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BrainCircuit, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { getBrainBrief } from "@/lib/api";

const ROWS = [
  { tone: "text-emerald-400", lum: "lum", label: "FATTO" },
  { tone: "text-rose-400", lum: "lum-rose", label: "SCARTATO" },
  { tone: "text-cyan-300", lum: "", label: "RADAR" },
];

const CHAR_MS = 14;

/* Riga a macchina da scrivere: i caratteri si accendono in sequenza. */
const TypeLine = ({ text, startDelay, row, testid, active }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    setN(0);
    let iv = null;
    const to = setTimeout(() => {
      iv = setInterval(() => {
        setN((v) => {
          if (v >= text.length) { clearInterval(iv); return v; }
          return v + 2;
        });
      }, CHAR_MS * 2);
    }, startDelay);
    return () => { clearTimeout(to); if (iv) clearInterval(iv); };
  }, [text, startDelay]);
  const done = n >= text.length;
  return (
    <p data-testid={testid} className="font-mono text-[13px] md:text-sm leading-relaxed text-foreground/85">
      <span className={cn("mr-3 text-[9px] font-bold tracking-[0.24em]", row.tone, row.lum)}>{row.label}</span>
      {text.slice(0, n)}
      {active && !done && <span className="inline-block w-2 h-3.5 ml-0.5 align-middle bg-emerald-400/80 animate-pulse" />}
    </p>
  );
};

export const BrainBrief = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    try {
      setData(await getBrainBrief(force));
    } catch { /* silenzioso: il briefing non deve mai bloccare la dashboard */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const lines = data?.lines || [];
  const stats = data?.stats;

  // ogni riga parte quando la precedente ha quasi finito di scriversi
  const delays = useMemo(() => {
    let acc = 350;
    return lines.map((l) => {
      const d = acc;
      acc += l.length * CHAR_MS + 500;
      return d;
    });
  }, [lines]);

  return (
    <motion.section
      data-testid="brain-brief"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="rounded-lg border border-border bg-[#111113] p-4 md:p-5"
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <span className="flex items-center gap-2">
          <BrainCircuit className="h-3.5 w-3.5 text-emerald-400/80" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/75">
            Briefing del Cervello
          </span>
          <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground/70">{data?.day || "—"}</span>
        </span>
        <div className="flex items-center gap-3">
          {stats && (
            <span data-testid="brain-brief-stats" className="hidden md:flex items-center gap-3 font-mono text-[9px] uppercase tracking-wider text-muted-foreground/80">
              <span>{stats.verdicts48h} valutate</span>
              <span className="text-rose-400/80">{stats.rejected48h} scartate</span>
              <span className="text-emerald-400/90">{stats.paperActive + stats.liveActive} attive</span>
              {stats.funnel30d?.hitRatePct != null && (
                <span data-testid="brain-brief-hitrate" className="text-amber-300/90">
                  hit-rate 30g {stats.funnel30d.promoted}/{stats.funnel30d.evaluated} ({stats.funnel30d.hitRatePct}%)
                </span>
              )}
            </span>
          )}
          <button
            type="button"
            data-testid="brain-brief-refresh"
            onClick={() => load(true)}
            title="Rigenera il briefing"
            className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:text-foreground hover:border-muted-foreground/50"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {loading && !lines.length
          ? (
            <div className="space-y-3">
              <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-400/70 animate-pulse">
                &gt; il cervello sta generando il briefing<span className="animate-pulse">_</span>
              </p>
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-5 animate-pulse rounded bg-muted/50" style={{ animationDelay: `${i * 150}ms`, width: `${90 - i * 8}%` }} />
              ))}
            </div>
          )
          : lines.map((line, i) => (
              <TypeLine key={`${data?.generatedAt}-${i}`} text={line} startDelay={delays[i]}
                row={ROWS[i] || ROWS[2]} testid={`brain-brief-line-${i}`} active />
            ))}
      </div>
      {(stats?.funnel30d?.topReasons?.length ?? 0) > 0 && (
        <p data-testid="brain-brief-reasons" className="mt-3 font-mono text-[10px] text-muted-foreground/70">
          scarti 30g: {stats.funnel30d.topReasons.map((r) => `${r.reason} ×${r.n}`).join(" · ")}
        </p>
      )}
    </motion.section>
  );
};

export default BrainBrief;
